import { useParams } from 'react-router';
import { Footer, TitleBar } from '../../../../components';
import { useBusinessFunctionData } from '../../../../hooks/business-functions/queries';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';
import { useUserContext } from '../../../../context/PermissionsContext';
import {
  GET_BUSINESS_FUNCTION_WORKAROUNDS,
  SET_BUSINESS_FUNCTION_WORKAROUND,
} from './queries';
import { useMutation } from '@apollo/client';
import { useBusinessFunctionApplicationWorkarounds } from './hooks';

import { commonColumns } from '../shared/workarounds/columns';
import { EditableColumnType } from '../shared/workarounds';
import { tableSortingMethods } from '../../../../utils';
import { useLocationName } from '../../../../hooks';
import { BusinessFunctionWorkaround } from '../../../../shared/types/business-functions-workarounds';
import {
  WORKAROUND_TYPES,
  WORKAROUND_DISPLAY_LABELS,
} from '../../../../shared/types/business-functions-workarounds';
import { useWorkaroundForm } from '../../../../hooks/workarounds/useWorkaroundForm';

const BusinessFunctionWorkaroundsItApplications = () => {
  const { locationId, businessFunctionId } = useParams<{
    locationId: string;
    businessFunctionId: string;
  }>();
  const { locationName } = useLocationName(locationId);
  const { businessFunctionName } = useBusinessFunctionData(businessFunctionId);

  const { canEdit } = useUserContext();

  const columns: EditableColumnType[] = [
    {
      title: 'IT Application',
      dataIndex: 'itApplicationName',
      key: 'itApplicationName',
      editable: false,
      defaultSortOrder: 'ascend',
      sorter: (a: any, b: any) =>
        tableSortingMethods.string(a.itApplicationName, b.itApplicationName),
      render: (text: string) =>
        text ? (
          <span>{text}</span>
        ) : (
          <span className="text-[#BABABA]">N/A</span>
        ),
    },
    ...commonColumns,
  ];

  const { data: workarounds, loading } =
    useBusinessFunctionApplicationWorkarounds(
      businessFunctionId ? [businessFunctionId] : [],
      locationId ?? '',
    );

  const [setWorkaround, { loading: updateLoading }] = useMutation(
    SET_BUSINESS_FUNCTION_WORKAROUND,
    {
      refetchQueries: [GET_BUSINESS_FUNCTION_WORKAROUNDS],
    },
  );

  const { formInstance, formFields } = useWorkaroundForm();

  const modifyVariables = ({ currentRecord, values }: ModifyVariablesArgs) => {
    const isInfinite =
      values.runtimeHours ===
      WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.INFINITE];
    const isNoWorkaround =
      values.runtimeHours ===
      WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.NO_WORKAROUND];
    const shouldSendRuntimeHours = isInfinite || isNoWorkaround;

    let normalizedRuntimeHours;

    if (!shouldSendRuntimeHours) {
      normalizedRuntimeHours = values.runtimeHours
        ? Number(values.runtimeHours)
        : null;
    }

    return {
      businessFunctionUuid: businessFunctionId,
      itApplicationUuid: currentRecord?.itApplicationId,
      locationUuid: locationId,
      isWorkaroundNeeded: Boolean(values.isWorkaroundNeeded),
      workaroundDefined: values.workaroundDefined,
      runtimeHours: normalizedRuntimeHours,
      maxDataLossHours: values.maxDataLossHours
        ? Number(values.maxDataLossHours)
        : null,
      workaroundDescription: values.workaroundDescription || null,
    };
  };

  return (
    <div className="p-2">
      <TitleBar
        title="Location Business Function Workarounds"
        subtitle={`${locationName} / ${businessFunctionName} Applications`}
        brief={`Capture and describe how the ${businessFunctionName} Function can work around a disruption of the following critical applications.`}
      />
      <TableV3
        columns={columns}
        data={workarounds}
        loading={loading || updateLoading}
        title={(currentRecord: BusinessFunctionWorkaround) =>
          `${currentRecord?.itApplicationName} / Edit Workaround`
        }
        formFields={formFields}
        updateGQL={setWorkaround}
        modifyVariables={modifyVariables}
        editable
        editableOptions={{
          update: canEdit,
          delete: false,
        }}
        hideTableHeading
        formInstance={formInstance}
      />
      <Footer
        backPath={`/cyber/location-level/${locationId}/business-functions-workarounds`}
      />
    </div>
  );
};

export default BusinessFunctionWorkaroundsItApplications;
